<template>
    <div class="sms-channel" :class="{ 'sms-channel--setup': inSetup }">
        <template v-if="!statusLoaded">
            <div class="sms-channel__loading">
                <v-progress-circular indeterminate size="26" width="2" color="#525AD5" />
                <span>Проверяем подключение…</span>
            </div>
        </template>

        <template v-else-if="inSetup">
            <ol class="sms-channel__progress" aria-label="Этапы подключения SMS">
                <li
                    v-for="(label, index) in stepLabels"
                    :key="label"
                    :class="{
                        'is-done': showRecommendations || index < setupStep,
                        'is-current': !showRecommendations && index === setupStep,
                    }"
                    :aria-current="!showRecommendations && index === setupStep ? 'step' : undefined"
                >
                    <span class="sms-channel__progress-num">{{ index + 1 }}</span>
                    <span class="sms-channel__progress-label">{{ label }}</span>
                </li>
            </ol>

            <h3 class="sms-channel__heading">{{ showRecommendations ? 'Выберите пакет SMS' : stepHeading }}</h3>
            <p v-if="stepLead && !showRecommendations" class="sms-channel__lead">{{ stepLead }}</p>

            <template v-if="showRecommendations">
                <div class="sms-channel__reco">
                    <span aria-hidden="true">
                        <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" /></svg>
                    </span>
                    <div>
                        <b>Рекомендуем пакет от 1 000 SMS в месяц</b>
                        <span>
                            Так SMS DH сможет стабильно отправлять сервисные сообщения без
                            неожиданного перерасхода.
                        </span>
                    </div>
                </div>
                <div class="sms-channel__transfer">
                    <b>Если у вашего оператора нет выгодного тарифа</b>
                    <span>
                        Подайте заявление на перенос номера к другому оператору. Номер сохранится, а вы
                        получите более дешёвый и предсказуемый способ связи через SMS DH.
                    </span>
                </div>
                <span class="sms-channel__disclaimer">
                    Цены и состав пакетов могут отличаться по регионам и меняться. Перед подключением
                    проверьте актуальные условия у оператора.
                </span>
            </template>

            <template v-else-if="setupStep === 0">
                <p class="sms-channel__android">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="2" width="14" height="20" rx="2" /><path d="M12 18h.01" /></svg>
                    <strong>Приложение работает только на Android.</strong>
                </p>
                <div class="sms-channel__download">
                    <img v-if="apkQrSrc" :src="apkQrSrc" alt="QR-код загрузки приложения SMS DH" />
                    <div v-else class="sms-channel__qr-wait sms-channel__qr-wait--apk">
                        <v-progress-circular indeterminate size="22" width="2" color="#525AD5" />
                    </div>
                    <div>
                        <strong>Откройте на Android</strong>
                        <p>Наведите камеру телефона на QR-код.</p>
                        <a class="sms-channel__link" :href="apkUrl" target="_blank" rel="noopener noreferrer">
                            Скачать APK напрямую
                        </a>
                    </div>
                </div>
                <a class="sms-channel__mobile-download" :href="apkUrl" target="_blank" rel="noopener noreferrer">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 15V3" /><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="m7 10 5 5 5-5" /></svg>
                    Скачать SMS DH
                </a>
                <section class="sms-channel__handoff">
                    <h3>Как установить APK</h3>
                    <ol>
                        <li>Скачайте APK на Android.</li>
                        <li>Откройте файл. При запросе разрешите установку из этого браузера.</li>
                        <li>На некоторых устройствах для установки нужно временно отключить <strong>Play Защиту</strong>.</li>
                        <li>Нажмите <strong>«Установить»</strong>, затем <strong>«Открыть»</strong>. После установки снова включите Play Защиту.</li>
                    </ol>
                </section>
            </template>

            <template v-else-if="setupStep === 1">
                <div class="sms-channel__pair">
                    <div class="sms-channel__qr-box">
                        <img v-if="qrDataUrl" :src="qrDataUrl" alt="QR-код подключения SMS DH" />
                        <div v-else class="sms-channel__qr-wait">
                            <v-progress-circular indeterminate size="22" width="2" color="#525AD5" />
                            <span>{{ qrError || 'Готовим QR-код…' }}</span>
                            <button v-if="qrError" type="button" class="sms-channel__text-btn" @click="$emit('reload-qr')">
                                Обновить
                            </button>
                        </div>
                    </div>
                    <ol>
                        <li>Откройте приложение SMS DH в телефоне.</li>
                        <li>Нажмите <strong>«Подключить по QR»</strong>.</li>
                        <li>Разрешите во всплывающих окнах все действия.</li>
                        <li>Отсканируйте код.</li>
                    </ol>
                </div>
                <p class="sms-channel__condition">
                    {{ pairHint || 'Если вы на том же телефоне, откройте этот шаг на компьютере.' }}
                </p>
            </template>

            <template v-else-if="setupStep === 2">
                <fieldset v-if="simCards.length > 1" class="sms-channel__sims">
                    <legend>SIM-карта для SMS</legend>
                    <label v-for="sim in simCards" :key="sim.simNumber" class="sms-channel__sim-pick">
                        <input
                            type="radio"
                            name="sms-setup-sim"
                            :value="sim.simNumber"
                            :checked="isSimChosen(sim.simNumber)"
                            :disabled="simSaving"
                            @change="$emit('select-sim', sim.simNumber)"
                        />
                        <span>SIM {{ sim.simNumber }}</span>
                    </label>
                </fieldset>
                <div v-else class="sms-channel__single-sim">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 14v4" /><path d="M14.172 2a2 2 0 0 1 1.414.586l3.828 3.828A2 2 0 0 1 20 7.828V20a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" /><path d="M8 14h8" /><rect x="8" y="10" width="8" height="8" rx="1" /></svg>
                    <strong>SIM {{ simNumber || 1 }}</strong>
                </div>
                <p v-if="simError" class="sms-channel__field-error">{{ simError }}</p>
                <label class="sms-channel__field" :class="{ 'is-error': phoneFieldError }">
                    <span>Рабочий номер SIM-карты</span>
                    <input
                        :value="phoneInput"
                        v-maska="phoneMask"
                        type="tel"
                        inputmode="tel"
                        autocomplete="tel"
                        placeholder="+7 (999) 123-45-67"
                        :aria-invalid="phoneFieldError ? 'true' : 'false'"
                        @input="onPhoneInput"
                    />
                    <small>{{ phoneFieldError || 'Клиенты будут получать SMS с этого номера.' }}</small>
                </label>
            </template>

            <template v-else-if="setupStep === 3">
                <ul class="sms-channel__permissions">
                    <li v-for="item in permissionItems" :key="item.title">
                        <strong>{{ item.title }}</strong>
                        <p>{{ item.copy }}</p>
                    </li>
                </ul>
            </template>

            <template v-else>
                <div class="sms-channel__note is-success">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" /></svg>
                    <span>
                        Рабочий номер: <strong>{{ workPhoneLabel }}</strong>
                        <template v-if="simNumber"> · SIM {{ simNumber }}</template>
                    </span>
                </div>
                <label class="sms-channel__field" :class="{ 'is-error': testPhoneError }">
                    <span>Номер для проверки</span>
                    <input
                        v-model="testPhoneInput"
                        v-maska="phoneMask"
                        type="tel"
                        inputmode="tel"
                        autocomplete="off"
                        placeholder="+7 (999) 765-43-21"
                        :readonly="testSent"
                        :aria-invalid="testPhoneError ? 'true' : 'false'"
                        @input="testPhoneError = ''"
                    />
                    <small>{{ testPhoneError || 'Проверьте, что SMS доходит на этот номер.' }}</small>
                </label>
                <div v-if="testSent" class="sms-channel__note is-success">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" /><path d="m21.854 2.147-10.94 10.939" /></svg>
                    <span>
                        <strong>Проверьте телефон.</strong>
                        Если SMS не пришло, посмотрите баланс SIM, интернет и разрешения приложения.
                    </span>
                </div>
                <div v-if="testFailed" class="sms-channel__note is-error">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M12 8v4" /><path d="M12 16h.01" /></svg>
                    <span>
                        <strong>{{ testFailures >= 2 ? 'SMS снова не дошло.' : 'SMS не получено.' }}</strong>
                        {{
                            testFailures >= 2
                                ? 'Обратитесь в поддержку — поможем проверить подключение.'
                                : 'Проверьте баланс SIM, интернет и разрешения приложения и повторите проверку.'
                        }}
                    </span>
                </div>
                <div v-if="testApiError" class="sms-channel__note is-error">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M12 8v4" /><path d="M12 16h.01" /></svg>
                    <span>Не удалось отправить тестовое SMS. Попробуйте ещё раз.</span>
                </div>
                <section v-if="testFailures >= 2" class="sms-channel__support">
                    <h3>Связаться с поддержкой</h3>
                    <div class="sms-channel__support-buttons">
                        <a class="sms-channel__btn sms-channel__btn--secondary" href="https://max.ru/id7707452371_bot" target="_blank" rel="noopener noreferrer">
                            <img class="sms-channel__support-icon" :src="maxLogo" alt="" />
                            Поддержка в MAX
                        </a>
                        <a class="sms-channel__btn sms-channel__btn--secondary" href="https://t.me/dataheroes_bot" target="_blank" rel="noopener noreferrer">
                            <img class="sms-channel__support-icon" :src="telegramLogo" alt="" />
                            Поддержка в Telegram
                        </a>
                    </div>
                </section>
            </template>

            <div class="sms-channel__actions">
                <template v-if="showRecommendations">
                    <button
                        type="button"
                        class="sms-channel__btn sms-channel__btn--ghost"
                        :disabled="stepBusy"
                        @click="showRecommendations = false"
                    >
                        Назад
                    </button>
                    <button
                        type="button"
                        class="sms-channel__btn sms-channel__btn--primary"
                        :disabled="stepBusy"
                        @click="finishWizard"
                    >
                        Завершить подключение
                    </button>
                </template>
                <button
                    v-else-if="setupStep > 0"
                    type="button"
                    class="sms-channel__btn sms-channel__btn--ghost"
                    :disabled="stepBusy"
                    @click="onBack"
                >
                    Назад
                </button>
                <div v-if="!showRecommendations && setupStep === 4 && testSent && !testFailed" class="sms-channel__confirm">
                    <button type="button" class="sms-channel__btn sms-channel__btn--danger" :disabled="stepBusy" @click="markTestFailed">
                        SMS не дошло
                    </button>
                    <button type="button" class="sms-channel__btn sms-channel__btn--primary" :disabled="stepBusy" @click="openRecommendations">
                        SMS получено
                    </button>
                </div>
                <div v-else-if="!showRecommendations && setupStep === 4 && testFailed" class="sms-channel__confirm">
                    <button type="button" class="sms-channel__btn sms-channel__btn--secondary" :disabled="stepBusy" @click="finishWizard">
                        Завершить позже
                    </button>
                    <button type="button" class="sms-channel__btn sms-channel__btn--primary" :disabled="stepBusy" @click="sendTest">
                        Отправить еще раз
                    </button>
                </div>
                <button
                    v-else-if="!showRecommendations"
                    type="button"
                    class="sms-channel__btn sms-channel__btn--primary"
                    :disabled="stepBusy"
                    @click="onPrimary"
                >
                    {{ stepBusy ? 'Проверяем…' : primaryLabel }}
                </button>
            </div>
        </template>

        <template v-else>
            <div class="sms-channel__badge" :class="offline || !isReady ? 'is-warn' : 'is-ok'">
                <span class="sms-channel__dot" />
                {{ chipText }}
            </div>
            <div class="sms-channel__card" :class="offline || !isReady ? 'is-warn' : 'is-ok'">
                <div v-if="offline || !isReady" class="sms-channel__offline-head">
                    <div class="sms-channel__mark is-warn">
                        <v-icon class="sms-channel__mark-icon" size="22" color="#A45F00">mdi-alert</v-icon>
                    </div>
                    <h3>{{ cardTitle }}</h3>
                </div>
                <div v-else class="sms-channel__card-head">
                    <div class="sms-channel__mark">
                        <v-icon class="sms-channel__mark-icon" size="22" color="#1A1A2E">mdi-check</v-icon>
                    </div>
                    <div>
                        <h3>{{ cardTitle }}</h3>
                        <p>{{ cardSubtitle }}</p>
                    </div>
                </div>
                <p v-if="offline || !isReady">{{ cardSubtitle }}</p>
                <div v-else class="sms-channel__rows">
                    <div class="sms-channel__row">
                        <span>Номер канала</span>
                        <strong>{{ workPhoneLabel }}</strong>
                    </div>
                    <div v-if="simNumber" class="sms-channel__row">
                        <span>SIM-карта</span>
                        <strong>SIM {{ simNumber }}</strong>
                    </div>
                    <div v-if="deviceName" class="sms-channel__row">
                        <span>Устройство</span>
                        <strong>{{ deviceName }}</strong>
                    </div>
                    <div class="sms-channel__row">
                        <span>Статус</span>
                        <strong class="is-ok">Онлайн</strong>
                    </div>
                </div>
            </div>

            <div v-if="offline || !isReady" class="sms-channel__facts">
                <div class="sms-channel__row">
                    <span>Номер канала</span>
                    <strong>{{ workPhoneLabel }}</strong>
                </div>
                <div v-if="simNumber" class="sms-channel__row">
                    <span>SIM-карта</span>
                    <strong>SIM {{ simNumber }}</strong>
                </div>
                <div class="sms-channel__row">
                    <span>Последняя связь</span>
                    <strong>{{ offline ? lastSeenText || 'нет данных' : 'Онлайн' }}</strong>
                </div>
            </div>

            <div v-if="simCards.length > 1" class="sms-channel__block">
                <div class="sms-channel__label">SIM-карта для отправки</div>
                <div class="sms-channel__sim-grid">
                    <button
                        v-for="sim in simCards"
                        :key="'connected-' + sim.simNumber"
                        type="button"
                        class="sms-channel__sim"
                        :class="{ 'sms-channel__sim--selected': isSimChosen(sim.simNumber) }"
                        :disabled="simSaving"
                        @click="$emit('select-sim', sim.simNumber)"
                    >
                        <span class="sms-channel__radio"></span>
                        <strong>SIM {{ sim.simNumber }}</strong>
                        <span>{{ sim.carrierName || 'Слот ' + (sim.slotIndex + 1) }}</span>
                        <span class="sms-channel__sim-phone">
                            {{ simPhones[String(sim.simNumber)] || 'номер не указан' }}
                        </span>
                        <span v-if="isSimChosen(sim.simNumber)" class="sms-channel__default">
                            По умолчанию
                        </span>
                    </button>
                </div>
                <p v-if="simError" class="sms-channel__error">{{ simError }}</p>
            </div>
            <p v-else-if="simError" class="sms-channel__error">{{ simError }}</p>

            <div v-if="!currentSimPhone" class="sms-channel__phone-save">
                <label class="sms-channel__field" :class="{ 'is-error': phoneFieldError }">
                    <span>Рабочий номер SIM-карты</span>
                    <input
                        :value="phoneInput"
                        v-maska="phoneMask"
                        type="tel"
                        inputmode="tel"
                        autocomplete="tel"
                        placeholder="+7 (999) 123-45-67"
                        :aria-invalid="phoneFieldError ? 'true' : 'false'"
                        @input="onPhoneInput"
                    />
                    <small>{{ phoneFieldError || 'Клиенты будут получать SMS с этого номера.' }}</small>
                </label>
                <button
                    type="button"
                    class="sms-channel__btn sms-channel__btn--primary"
                    :disabled="phoneSaving"
                    @click="savePhoneOnly"
                >
                    {{ phoneSaving ? 'Сохраняем…' : 'Сохранить' }}
                </button>
            </div>

            <div v-if="currentSimPhone" class="sms-channel__block sms-channel__test">
                <h2 class="sms-channel__test-title">Тестовое сообщение</h2>
                <p class="sms-channel__test-lead">
                    Номер, на который придет тестовое сообщение для проверки подключения бота:
                </p>
                <div class="sms-channel__test-row">
                    <label class="sms-channel__field" :class="{ 'is-error': testPhoneError }">
                        <input
                            v-model="testPhoneInput"
                            v-maska="phoneMask"
                            type="tel"
                            inputmode="tel"
                            autocomplete="off"
                            placeholder="+7 (987) 654-32-10"
                            :readonly="testSending"
                            :aria-invalid="testPhoneError ? 'true' : 'false'"
                            @input="testPhoneError = ''"
                        />
                    </label>
                    <button
                        type="button"
                        class="sms-channel__btn sms-channel__btn--outline"
                        :disabled="testSending"
                        @click="sendTest"
                    >
                        {{ testSending ? 'Отправляем…' : 'Отправить сообщение' }}
                    </button>
                </div>
                <small v-if="testPhoneError" class="sms-channel__field-error">{{ testPhoneError }}</small>
                <div v-if="testSent" class="sms-channel__note is-success">
                    <span>Тестовое SMS отправлено.</span>
                </div>
                <div v-if="testApiError" class="sms-channel__note is-error">
                    <span>Не удалось отправить тестовое SMS. Попробуйте ещё раз.</span>
                </div>
            </div>

            <div class="sms-channel__actions sms-channel__actions--end">
                <button
                    type="button"
                    class="sms-channel__btn sms-channel__btn--danger"
                    :disabled="disconnecting"
                    @click="$emit('disconnect')"
                >
                    {{ disconnecting ? 'Отключаем…' : 'Отключить' }}
                </button>
            </div>
        </template>
    </div>
</template>

<script>
import QRCode from 'qrcode'
import maxLogo from '@/assets/img/messenger-logo/max-logo.png'
import telegramLogo from '@/assets/img/messenger-logo/telegram-logo.png'
import phoneValidator, { isValidPhone } from '@/helpers/validators/phoneValidator'
import { hasSmsSavedNumber, resolveSmsWizardStep } from '@/helpers/channels/smsGateStatus'
import { logSmsPromoButton } from '@/helpers/ymLogger'

function digits(value) {
    return String(value || '').replace(/\D/g, '')
}

/**
 * Экран настроек SMS-канала.
 *
 * Презентационный: данные и действия приходят пропсами и событиями.
 * Пошаговый сценарий (установка → QR → номер → права → тест) живёт здесь,
 * запросы к шлюзу остаются в ChannelSettingsForm.
 */
export default {
    name: 'SmsChannelSettings',
    props: {
        statusLoaded: { type: Boolean, default: false },
        connected: { type: Boolean, default: false },
        offline: { type: Boolean, default: false },
        lastSeenText: { type: String, default: '' },
        deviceName: { type: String, default: '' },
        phone: { type: String, default: '' },
        formattedPhone: { type: String, default: '' },
        simCards: { type: Array, default: () => [] },
        simNumber: { type: Number, default: null },
        simPhones: { type: Object, default: () => ({}) },
        simSaving: { type: Boolean, default: false },
        simError: { type: String, default: '' },
        phoneInput: { type: String, default: '' },
        phoneSaving: { type: Boolean, default: false },
        phoneError: { type: String, default: '' },
        qrDataUrl: { type: String, default: '' },
        qrLoading: { type: Boolean, default: false },
        qrError: { type: String, default: '' },
        disconnecting: { type: Boolean, default: false },
        testSending: { type: Boolean, default: false },
        testStatus: { type: String, default: null },
        apkUrl: {
            type: String,
            default: () => {
                const isProd =
                    typeof window !== 'undefined' &&
                    window.location.hostname.includes('dataheroes')
                return isProd
                    ? 'https://storage.yandexcloud.net/dataheroes-sms-app/latest.apk'
                    : 'https://storage.yandexcloud.net/legendafiles/dh-sms-app/latest.apk'
            },
        },
    },
    data() {
        return {
            wizardActive: null,
            holdWizard: false,
            showRecommendations: false,
            setupStep: 0,
            apkQrSrc: '',
            pairHint: '',
            localPhoneError: '',
            pendingPhoneSave: false,
            testPhoneInput: '',
            testPhoneError: '',
            testSent: false,
            testFailed: false,
            testFailures: 0,
            testApiError: false,
            phoneMask: '+# (###) ###-##-#*',
            maxLogo,
            telegramLogo,
            stepLabels: ['Установка', 'QR-код', 'Номер SIM', 'Права', 'Тест SMS'],
            permissionItems: [
                { title: 'Телефон и SMS', copy: 'Разрешите доступ.' },
                { title: 'Уведомления', copy: 'Включите уведомления приложения.' },
                { title: 'Работа в фоне', copy: 'Разрешите и отключите приостановку приложения.' },
                { title: 'Батарея', copy: 'Выберите «Без ограничений».' },
                { title: 'Автозапуск', copy: 'Включите, если этот пункт есть на телефоне.' },
            ],
        }
    },
    computed: {
        inSetup() {
            if (!this.statusLoaded) return false
            if (this.wizardActive === true) return true
            if (this.wizardActive === false) return false
            return !(this.connected && this.currentSimPhone)
        },
        isReady() {
            return !this.offline && !!this.currentSimPhone
        },
        currentSimPhone() {
            const fromSim = this.simNumber ? this.simPhones?.[String(this.simNumber)] : ''
            return fromSim || this.phone || ''
        },
        hasSavedNumber() {
            return hasSmsSavedNumber({
                phone: this.phone,
                simPhones: this.simPhones,
                extras: [this.formattedPhone],
            })
        },
        workPhoneLabel() {
            if (this.formattedPhone) return this.formattedPhone
            return this.currentSimPhone || 'не указан'
        },
        phoneFieldError() {
            return this.localPhoneError || this.phoneError
        },
        chosenSimNumber() {
            const cards = this.simCards || []
            const current = this.simNumber
            const listed = cards.some(
                (sim) => current != null && current !== '' && Number(sim.simNumber) === Number(current)
            )
            if (listed) return Number(current)
            if (cards.length > 1 && cards[0]?.simNumber != null) return Number(cards[0].simNumber)
            return current
        },
        stepBusy() {
            return this.phoneSaving || this.simSaving || this.testSending
        },
        stepHeading() {
            return [
                'Установите SMS DH',
                'Отсканируйте QR-код',
                this.simCards.length > 1 ? 'Выберите SIM и укажите номер' : 'Укажите номер SIM-карты',
                'Проверьте, выданы ли приложению все права',
                'Отправьте тестовое SMS',
            ][this.setupStep]
        },
        stepLead() {
            return [
                'На рабочий телефон с SIM-картой.',
                'Свяжите телефон с проектом.',
                this.simCards.length > 1
                    ? 'Выберите SIM с пакетом SMS и укажите её номер.'
                    : 'В телефоне одна SIM-карта. Укажите её номер.',
                'Настройки Android → Приложения → SMS DH.',
                '',
            ][this.setupStep]
        },
        primaryLabel() {
            return [
                'Приложение установлено',
                'Проверить подключение',
                'Сохранить и продолжить',
                'Выдано',
                'Отправить тестовое SMS',
            ][this.setupStep]
        },
        chipText() {
            if (this.offline) return 'Требуется внимание'
            if (!this.currentSimPhone) return 'Требуется настройка'
            return 'Подключён'
        },
        cardTitle() {
            if (this.offline) return 'Телефон не выходит на связь'
            if (!this.currentSimPhone) return 'Укажите номер SIM-карты'
            return 'SMS DH подключён'
        },
        cardSubtitle() {
            if (this.offline) {
                return 'SMS не отправляются. Проверьте интернет, заряд телефона и работу приложения SMS DH.'
            }
            if (!this.currentSimPhone) {
                return 'Телефон на связи, но пока не указан номер SIM — сообщения не отправляются.'
            }
            return 'Канал готов к рассылкам и сервисным уведомлениям.'
        },
    },
    watch: {
        statusLoaded() {
            this.reconcile()
        },
        phone() {
            this.reconcile()
        },
        formattedPhone() {
            this.reconcile()
        },
        simNumber() {
            this.reconcile()
        },
        simPhones: {
            deep: true,
            handler() {
                this.reconcile()
            },
        },
        connected(value, previous) {
            if (previous && !value) {
                this.holdWizard = true
                this.wizardActive = true
                this.showRecommendations = false
                this.setupStep = 0
                this.resetTest()
                this.renderApkQr()
                return
            }
            if (value && previous === false) this.holdWizard = false
            if (value && this.wizardActive === true && this.setupStep < 2 && !this.hasSavedNumber) {
                this.pairHint = ''
                this.setupStep = 2
            }
            this.reconcile()
        },
        phoneSaving(value, previous) {
            if (previous && !value && this.pendingPhoneSave && !this.phoneError) {
                this.pendingPhoneSave = false
                this.localPhoneError = ''
                this.setupStep = 3
            }
            if (previous && !value && this.phoneError) {
                this.pendingPhoneSave = false
            }
        },
        testStatus(value) {
            const onConnectedCard = !this.inSetup
            if (!onConnectedCard && this.setupStep !== 4) return
            if (!value) {
                if (onConnectedCard) {
                    this.testSent = false
                    this.testApiError = false
                }
                return
            }
            if (value === 'success') {
                this.testSent = true
                this.testFailed = false
                this.testApiError = false
            } else if (value === 'error') {
                this.testSent = false
                this.testApiError = true
            }
        },
        apkUrl() {
            this.renderApkQr()
        },
        setupStep(step) {
            if (step === 0) this.renderApkQr()
        },
    },
    created() {
        this.reconcile()
    },
    mounted() {
        this.reconcile()
    },
    methods: {
        reconcile() {
            if (!this.statusLoaded || this.holdWizard) return
            const step = resolveSmsWizardStep(this.connected, this.hasSavedNumber)
            if (step === null) {
                // Номер только что сохранили в этом проходе — не закрывать мастер
                // до шагов «права» и «тест». Уже подключённый канал (шаг ещё не
                // ушёл дальше номера) показываем карточкой, а не установкой.
                if (this.pendingPhoneSave) return
                if (!this.showRecommendations && (this.wizardActive !== true || this.setupStep <= 2)) {
                    this.wizardActive = false
                    this.showRecommendations = false
                }
                return
            }
            if (this.wizardActive === null) {
                this.wizardActive = true
                this.setupStep = step
                if (this.setupStep === 0) this.renderApkQr()
            }
        },
        async renderApkQr() {
            if (!this.apkUrl) return
            try {
                this.apkQrSrc = await QRCode.toDataURL(this.apkUrl, {
                    margin: 1,
                    width: 256,
                    errorCorrectionLevel: 'M',
                })
            } catch {
                this.apkQrSrc = ''
            }
        },
        isSimChosen(simNumber) {
            if (this.chosenSimNumber == null || this.chosenSimNumber === '') return false
            return Number(simNumber) === Number(this.chosenSimNumber)
        },
        onPhoneInput(event) {
            this.localPhoneError = ''
            this.$emit('update:phoneInput', event.target.value)
        },
        openRecommendations() {
            this.showRecommendations = true
        },
        onBack() {
            if (this.setupStep <= 0 || this.stepBusy) return
            if (this.setupStep === 4) this.resetTest()
            this.setupStep -= 1
        },
        onPrimary() {
            if (this.stepBusy) return
            if (this.setupStep === 0) {
                this.setupStep = 1
                return
            }
            if (this.setupStep === 1) {
                if (this.connected) {
                    this.pairHint = ''
                    this.setupStep = 2
                    return
                }
                this.pairHint = 'Телефон ещё не подключён. Отсканируйте QR-код в приложении SMS DH.'
                this.$emit('reload-qr')
                return
            }
            if (this.setupStep === 2) {
                this.savePhoneAndContinue()
                return
            }
            if (this.setupStep === 3) {
                this.setupStep = 4
                return
            }
            this.sendTest()
        },
        savePhoneOnly() {
            if (!isValidPhone(this.phoneInput)) {
                this.localPhoneError = phoneValidator(this.phoneInput) || 'Введите номер: +7 и 10 цифр.'
                return
            }
            this.localPhoneError = ''
            this.$emit('save-phone')
        },
        savePhoneAndContinue() {
            if (!isValidPhone(this.phoneInput)) {
                this.localPhoneError = phoneValidator(this.phoneInput) || 'Введите номер: +7 и 10 цифр.'
                return
            }
            this.localPhoneError = ''
            this.pendingPhoneSave = true
            this.$emit('save-phone')
        },
        sendTest() {
            this.testPhoneError = ''
            this.testApiError = false
            this.testFailed = false
            if (!isValidPhone(this.testPhoneInput)) {
                this.testPhoneError = 'Введите номер: +7 и 10 цифр.'
                return
            }
            if (digits(this.testPhoneInput) === digits(this.currentSimPhone)) {
                this.testPhoneError = 'Для проверки укажите другой номер, не рабочую SIM.'
                return
            }
            this.testSent = false
            this.$emit('send-test', this.testPhoneInput)
        },
        markTestFailed() {
            if (!this.testSent) return
            this.testFailures += 1
            this.testSent = false
            this.testFailed = true
            this.testApiError = false
        },
        finishWizard() {
            // Кнопка «Завершить подключение»
            logSmsPromoButton('dh_sms_finish_setup')
            this.showRecommendations = false
            this.wizardActive = false
        },
        resetTest() {
            this.testSent = false
            this.testFailed = false
            this.testApiError = false
            this.testPhoneError = ''
        },
    },
}
</script>

<style lang="sass" scoped>
.sms-channel
    width: 100%
    min-width: 0
    color: #1A1A2E
    svg
        width: 20px
        height: 20px
        fill: none
        stroke: currentColor
        stroke-width: 2
        stroke-linecap: round
        stroke-linejoin: round
        flex: none

    &__loading
        display: flex
        align-items: center
        justify-content: center
        gap: 12px
        padding: 48px 0
        color: #596278
        font-size: 13px

    &__progress
        display: grid
        grid-template-columns: repeat(5, minmax(0, 1fr))
        gap: 6px
        margin: 0 0 18px
        padding: 0
        list-style: none
        li
            min-width: 0
            padding: 8px 4px
            border: 1px solid #E5E8F0
            border-radius: 11px
            background: #FAFBFF
            text-align: center
            &.is-current
                border-color: #C9CEFF
                background: #EEF0FF
            &.is-done
                border-color: #DDE8A0
                background: #FBFDEB

    &__progress-num
        width: 22px
        height: 22px
        margin: 0 auto
        border-radius: 7px
        background: #E9ECF3
        color: #7A8196
        display: grid
        place-items: center
        font-size: 10px
        font-weight: 800
        .is-current &
            background: #525AD5
            color: #fff
        .is-done &
            background: #D4EC38
            color: #1A1A2E

    &__progress-label
        display: block
        margin-top: 5px
        font-size: 9px
        font-weight: 750
        line-height: 1.25
        color: #7A8196
        overflow-wrap: anywhere
        .is-current &
            color: #525AD5

    &__heading
        margin: 0 0 16px
        font-size: 22px
        line-height: 1.25
        letter-spacing: -0.03em
        font-weight: 800

    &__lead
        margin: 0 0 20px
        font-size: 13px
        line-height: 1.55
        color: #596278

    &__android
        display: flex
        align-items: flex-start
        gap: 10px
        margin: 0
        color: #525AD5
        font-size: 13px
        line-height: 1.5

    &__download
        display: flex
        align-items: center
        gap: 20px
        margin-top: 20px
        img, .sms-channel__qr-wait--apk
            width: 128px
            height: 128px
            flex: none
        strong
            font-size: 13px
        p
            margin: 8px 0
            font-size: 12px
            line-height: 1.5
            color: #596278

    &__link
        font-size: 12px
        color: #525AD5
        text-underline-offset: 3px

    &__mobile-download
        display: none

    &__handoff
        margin-top: 24px
        padding-top: 20px
        border-top: 1px solid #E5E8F0
        h3
            margin: 0 0 12px
            font-size: 14px
        ol
            margin: 0
            padding-left: 20px
            font-size: 13px
            line-height: 1.6
            color: #596278
            li + li
                margin-top: 10px
            strong
                color: #1A1A2E

    &__pair
        display: flex
        flex-direction: column
        align-items: center
        gap: 20px
        ol
            align-self: stretch
            margin: 0
            padding-left: 20px
            font-size: 13px
            line-height: 1.6
            color: #30354B
            li + li
                margin-top: 10px

    &__qr-box,
    &__qr-wait
        width: 184px
        height: 184px
        border: 1px solid #E5E8F0
        border-radius: 16px
        padding: 14px
        background: #fff
        display: flex
        flex-direction: column
        align-items: center
        justify-content: center
        gap: 8px
        text-align: center
        color: #596278
        font-size: 12px
        img
            width: 100%
            height: 100%
            display: block

    &__qr-wait--apk
        border: 0
        padding: 0

    &__text-btn
        border: 0
        background: transparent
        color: #525AD5
        font-weight: 700
        font-size: 12px
        cursor: pointer

    &__condition
        margin: 20px 0 0
        font-size: 12px
        line-height: 1.5
        color: #596278

    &__sims
        margin: 16px 0 0
        padding: 0
        border: 0
        min-width: 0
        legend
            margin-bottom: 10px
            padding: 0
            font-size: 13px
            font-weight: 700

    &__sim-pick
        display: flex
        align-items: center
        gap: 12px
        margin-bottom: 8px
        padding: 14px 16px
        border-radius: 12px
        background: #EEF0FF
        cursor: pointer
        input
            accent-color: #525AD5
        span
            font-size: 14px
            font-weight: 700

    &__phone-save
        display: grid
        gap: 12px
        margin-top: 18px
        .sms-channel__field
            margin-top: 0

    &__block
        width: 100%
        margin-top: 18px

    &__test-title
        margin: 0 0 16px
        font-size: 18px
        font-weight: 600
        line-height: 24px
        letter-spacing: -0.2px
        color: #2A3B51

    &__test-lead
        margin: 0 0 8px
        font-size: 14px
        line-height: 1.4
        color: #2A3B51DE

    &__test-row
        display: flex
        align-items: center
        flex-wrap: nowrap
        gap: 12px
        width: 100%
        .sms-channel__field
            flex: 1 1 auto
            width: auto
            min-width: 0
            margin-top: 0
            input
                height: 48px
                border-radius: 12px
                box-sizing: border-box
        .sms-channel__btn
            flex: 0 0 auto
            margin: 0

    &__label
        margin-bottom: 8px
        font-size: 13px
        font-weight: 600
        color: #2A3B51

    &__error
        margin: 8px 0 0
        font-size: 12px
        color: #E8183C

    &__sim-grid
        display: grid
        grid-template-columns: 1fr 1fr
        gap: 10px

    &__sim
        position: relative
        text-align: left
        padding: 12px
        border-radius: 12px
        border: 1.5px solid rgba(42, 59, 81, 0.16)
        background: #fff
        cursor: pointer
        &:disabled
            opacity: 0.6
            cursor: default
        strong
            display: block
            font-size: 13px
            color: #2A3B51
        span:not(.sms-channel__radio):not(.sms-channel__default)
            display: block
            margin-top: 4px
            font-size: 11px
            color: rgba(42, 59, 81, 0.6)
        &--selected
            border-color: #378EFF
            background: rgba(55, 142, 255, 0.06)

    &__sim-phone
        display: block
        margin-top: 6px
        font-size: 11px
        font-weight: 600
        color: #2A3B51

    &__default
        display: inline-block
        margin-top: 8px
        padding: 3px 7px
        border-radius: 999px
        background: rgba(76, 175, 80, 0.14)
        color: #2E7D32
        font-size: 10px
        font-weight: 700

    &__radio
        position: absolute
        top: 12px
        right: 12px
        width: 16px
        height: 16px
        border-radius: 50%
        border: 2px solid rgba(42, 59, 81, 0.28)

    &__sim--selected &__radio
        border-color: #378EFF
        box-shadow: inset 0 0 0 3px #fff
        background: #378EFF

    &__single-sim
        display: flex
        align-items: center
        gap: 12px
        padding: 14px 16px
        border-radius: 12px
        background: #EEF0FF
        font-size: 14px
        svg
            color: #525AD5

    &__field
        display: grid
        gap: 8px
        margin-top: 20px
        span
            font-size: 13px
            font-weight: 700
        input
            width: 100%
            min-width: 0
            height: 48px
            border: 1px solid #8b93a5
            border-radius: 12px
            padding: 12px 14px
            color: #1A1A2E
            background: #fff
            font-size: 16px
            &:focus
                outline: 3px solid rgba(82, 90, 213, 0.18)
                border-color: #525AD5
            &[readonly]
                background: #F5F6FA
        small
            min-height: 18px
            font-size: 12px
            line-height: 1.5
            color: #596278
        &.is-error
            input
                border-color: #b3203d
            small
                color: #b3203d

    &__field-error
        margin: 8px 0 0
        color: #b3203d
        font-size: 12px

    &__permissions
        list-style: none
        margin: 0
        padding: 0
        li
            padding: 14px 0
            border-bottom: 1px solid #E5E8F0
            &:first-child
                padding-top: 0
            &:last-child
                border: 0
                padding-bottom: 0
        strong
            font-size: 14px
        p
            margin: 4px 0 0
            font-size: 13px
            line-height: 1.5
            color: #596278

    &__reco
        display: grid
        grid-template-columns: 36px minmax(0, 1fr)
        gap: 12px
        align-items: start
        padding: 16px
        border: 1px solid #CDEBD9
        border-radius: 16px
        background: #EDF8F1
        > span
            width: 36px
            height: 36px
            display: grid
            place-items: center
            border-radius: 12px
            background: #fff
            color: #176d44
        b
            display: block
            color: #1A1A2E
            font-size: 14px
        > div span
            display: block
            margin-top: 5px
            color: #587364
            font-size: 13px
            line-height: 1.55

    &__transfer
        margin-top: 16px
        padding: 16px
        border: 1px solid #D8DFF4
        border-radius: 16px
        background: #F7F8FF
        b
            display: block
            color: #1A1A2E
            font-size: 14px
        span
            display: block
            margin-top: 6px
            color: #536078
            font-size: 13px
            line-height: 1.55

    &__disclaimer
        display: block
        margin-top: 10px
        color: #7A839A
        font-size: 12px
        line-height: 1.5

    &__note
        display: flex
        align-items: flex-start
        gap: 10px
        margin-top: 16px
        padding: 14px
        border-radius: 12px
        font-size: 13px
        line-height: 1.55
        &.is-success
            background: #edf8f1
            color: #176d44
        &.is-error
            background: #fff1f3
            color: #b3203d

    &__support
        margin-top: 24px
        padding-top: 20px
        border-top: 1px solid #E5E8F0
        h3
            margin: 0 0 12px
            font-size: 14px

    &__support-buttons
        display: grid
        grid-template-columns: 1fr 1fr
        gap: 8px

    &__support-icon
        width: 18px
        height: 18px
        object-fit: contain
        flex-shrink: 0

    &__actions
        display: flex
        align-items: center
        justify-content: space-between
        gap: 8px
        margin-top: 24px
        &--end
            justify-content: flex-end

    &__confirm
        display: flex
        align-items: center
        justify-content: flex-end
        gap: 8px
        flex: 1
        min-width: 0
        margin-left: auto
        .sms-channel__btn--primary
            margin-left: 0

    &__btn
        min-height: 44px
        height: auto
        padding: 11px 14px
        border-radius: 13px
        border: 0
        font-weight: 800
        font-size: 12px
        line-height: 1.4
        cursor: pointer
        display: inline-flex
        align-items: center
        justify-content: center
        gap: 8px
        text-decoration: none
        &:disabled
            background: #E5E7ED !important
            color: #A6ADBC !important
            box-shadow: none
            cursor: not-allowed
        &--primary
            margin-left: auto
            background: #525AD5
            color: #fff
            box-shadow: 0 8px 20px rgba(82, 90, 213, 0.22)
            &:hover:not(:disabled)
                background: #454CC0
        &--ghost
            background: transparent
            color: #596278
        &--secondary
            background: #fff
            border: 1px solid #D6DBE6
            color: #30354B
        &--danger
            background: #fff
            border: 1px solid #FFD0DA
            color: #E8183C
        &--outline
            box-sizing: border-box
            height: 48px
            min-height: 48px
            margin-left: 0
            padding: 0 16px
            border-radius: 12px
            border: 1px solid #525AD5
            background: #fff
            color: #525AD5
            box-shadow: none
            font-weight: 500
            font-size: 14px
            line-height: 1
            white-space: nowrap
            &:hover:not(:disabled)
                background: rgba(82, 90, 213, 0.06)

    &__badge
        display: inline-flex
        align-items: center
        gap: 7px
        margin-bottom: 16px
        padding: 7px 10px
        border-radius: 999px
        font-size: 10px
        font-weight: 800
        &.is-ok
            background: #F5FAD7
            color: #587000
        &.is-warn
            background: #FFF4E3
            color: #C7770B

    &__dot
        width: 7px
        height: 7px
        border-radius: 50%
        background-color: #587000
        .is-warn &
            background-color: #C7770B

    &__card
        padding: 16px
        border-radius: 18px
        border: 1px solid #E6F18B
        background: #F5FAD7
        &.is-warn
            background: #FFF4E3
            border-color: #FFE0AA
        h3
            margin: 1px 0 4px
            font-size: 16px
        p
            margin: 0
            font-size: 11px
            line-height: 1.45
            color: #587000
        &.is-warn p
            margin: 8px 0 0
            font-size: 11px
            line-height: 1.5
            color: #765116
        &.is-warn h3
            margin: 0
            font-size: 15px

    &__offline-head
        display: flex
        align-items: center
        gap: 10px
        .sms-channel__mark
            width: 42px
            height: 42px
            border-radius: 14px

    &__facts
        display: grid
        gap: 11px
        margin-top: 12px
        padding: 16px
        border: 1px solid #E5E8F0
        border-radius: 18px
        background: #fff
        box-shadow: 0 4px 16px rgba(26, 26, 46, 0.07)

    &__card-head
        display: flex
        align-items: flex-start
        gap: 12px

    &__mark
        width: 46px
        height: 46px
        border-radius: 15px
        background: #D4EC38
        color: #1A1A2E
        display: grid
        place-items: center
        flex: none
        &.is-warn
            background: #FFE6BC
            color: #A45F00
        ::v-deep .sms-channel__mark-icon.v-icon
            color: #1A1A2E !important
        &.is-warn ::v-deep .sms-channel__mark-icon.v-icon
            color: #A45F00 !important

    &__rows
        display: grid
        gap: 11px
        margin-top: 16px
        padding-top: 14px
        border-top: 1px solid rgba(88, 112, 0, 0.16)

    &__row
        display: flex
        justify-content: space-between
        gap: 16px
        font-size: 11px
        span
            color: #7A8196
        strong
            text-align: right
            overflow-wrap: anywhere
            &.is-ok
                color: #587000
            &.is-warn
                color: #C7770B

@media (max-width: 489px)
    .sms-channel__test-row
        flex-direction: column
        align-items: stretch
        gap: 10px
        .sms-channel__field
            flex: none
            width: 100%
        .sms-channel__btn--outline
            width: 100%

@media (max-width: 720px)
    .sms-channel
        &__heading
            font-size: 21px
        &__download
            display: none
        &__mobile-download
            display: flex
            align-items: center
            justify-content: center
            gap: 8px
            margin-top: 20px
            min-height: 44px
            border-radius: 13px
            background: #525AD5
            color: #fff
            font-size: 12px
            font-weight: 800
            text-decoration: none
        &__support-buttons
            grid-template-columns: 1fr
        &__confirm
            flex-wrap: wrap
            .sms-channel__btn
                flex: 1
                font-size: 11px
                padding: 10px 8px

@media (max-width: 380px)
    .sms-channel__sim-grid
        grid-template-columns: 1fr

@media (max-width: 359px)
    .sms-channel__actions
        flex-wrap: wrap
    .sms-channel__confirm
        flex-basis: 100%
</style>
